/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useRef } from 'react'
import { frame } from '../flight/state.ts'

// The copy layer (WEBSITE_COPY.md, scroll order) as scrollytelling cards: each
// card rises in when its beat begins, STAYS pinned while the beat plays, and
// hands off to the next. Real DOM over the canvas (law 8), always present for
// crawlers and screen readers regardless of opacity; visibility is driven from
// the same damped progress the world runs on. Accents rotate through the
// randommonkey.io ink dots (cyan / magenta / yellow).

interface Card {
  id: string
  from: number
  to: number
  cls: string
  el: React.ReactNode
}

const CARDS: Card[] = [
  {
    id: 'opening',
    from: -1,
    to: 0.105,
    cls: 'card card-opening accent-c',
    el: (
      <header>
        <h1>Random Monkey</h1>
        <p className="statement">Enough monkeys. Enough time.</p>
        <p className="body">
          A monkey at a typewriter will eventually write Shakespeare. Infinitely true, infinitely
          unuseful&hellip; until now.
        </p>
      </header>
    ),
  },
  {
    id: 'many',
    from: 0.12,
    to: 0.3,
    cls: 'card accent-m',
    el: (
      <>
        <h2>Randomness produces everything and nothing.</h2>
        <p className="body">
          It&rsquo;s us who drive meaning, collectively. Every symbol you need already exists
          somewhere&hellip; next to a billion that look just like it and say the wrong thing. More
          generation doesn&rsquo;t get you closer to the one you wanted. It buries it deeper.
        </p>
      </>
    ),
  },
  {
    id: 'routing',
    from: 0.315,
    to: 0.465,
    cls: 'card accent-y',
    el: (
      <>
        <h2>Something has to catch it.</h2>
        <p className="body">
          Raw output goes nowhere on its own. It has to be captured, routed, and given a shape
          before a person or an agent can do anything with it. Most tools stop at the generating.
          The work that matters starts after.
        </p>
      </>
    ),
  },
  {
    id: 'turn',
    from: 0.48,
    to: 0.65,
    cls: 'card accent-c',
    el: (
      <>
        <h2>Structure is the scarce thing.</h2>
        <p className="body">
          Generation is cheap and getting cheaper. What&rsquo;s hard is data that knows what it is:
          ordered, permissioned by default, carrying a record of where it came from and who changed
          it. That&rsquo;s what lets you hand real work to anyone &mdash; a new hire, a client, an
          agent running overnight &mdash; and still trust what comes back.
        </p>
        <p className="kicker">That&rsquo;s the part we build.</p>
      </>
    ),
  },
  {
    id: 'out',
    from: 0.665,
    to: 0.78,
    cls: 'card accent-m',
    el: (
      <>
        <h2>Typed. Structured. Yours.</h2>
        <p className="body">
          Once the data underneath is solid, everything downstream is a view of it. Charts,
          reports, dashboards, forms, public sites &mdash; all generated from the source, none of
          them drifting from it.
        </p>
      </>
    ),
  },
  {
    id: 'becomes',
    from: 0.795,
    to: 0.905,
    cls: 'card accent-y',
    el: (
      <>
        <h2>What you build with it stays built.</h2>
        <p className="body">
          Open formats, on your machine or in your account. Every change is an event on a log, so
          the history is readable, reversible, portable. No export tax, no silos, no lock-in, no
          renting access to your own past work.
        </p>
      </>
    ),
  },
]

const PRODUCTS = [
  {
    accent: 'c',
    name: 'Datasketch',
    line: 'One place for your organization’s data, and everything you make from it.',
    href: 'https://datasketch.co',
  },
  {
    accent: 'm',
    name: 'MonkeyPlex',
    line: 'Docs, tables, chat and slides, where AI agents are actual coworkers.',
    href: 'https://monkeyplex.app',
  },
  {
    accent: 'y',
    name: 'Miqqa',
    line: 'An agent that does the data work — cleaning it, typing it, transforming it.',
    href: 'https://miqqa.ai',
  },
]

const ENTER = 0.018
const EXIT = 0.02

export function Copy() {
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let raf = 0
    const tick = () => {
      const p = frame.p
      const root = rootRef.current
      if (root) {
        for (const card of CARDS) {
          const el = root.querySelector<HTMLElement>(`[data-copy="${card.id}"]`)
          if (!el) continue
          const enter = card.from < 0 ? 1 : Math.min(1, Math.max(0, (p - card.from) / ENTER))
          const exit = Math.min(1, Math.max(0, (card.to - p) / EXIT))
          el.style.opacity = String(Math.min(enter, exit))
          el.style.transform = `translateY(${(1 - enter) * 22 - (1 - exit) * 16}px)`
        }
        const doors = root.querySelector<HTMLElement>('[data-copy="doors"]')
        if (doors) {
          // the doors arrive only once the rise is nearly complete — an ascent, not a footer
          const o = Math.min(1, Math.max(0, (p - 0.945) / 0.035))
          doors.style.opacity = String(o)
          doors.style.transform = `translateY(${(1 - o) * 26}px)`
          doors.style.pointerEvents = o > 0.5 ? 'auto' : 'none'
        }
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <div className="copy-root" ref={rootRef}>
      {/* persistent brand chrome — the randommonkey.io logo */}
      <a className="brand" href="/" aria-label="Random Monkey — back to the top">
        <img src="/assets/logo.png" alt="Random Monkey" width="124" height="48" />
      </a>
      {CARDS.map((c) => (
        <div
          key={c.id}
          data-copy={c.id}
          className={c.cls}
          style={{ opacity: c.id === 'opening' ? 1 : 0 }}
        >
          {c.el}
        </div>
      ))}
      <div data-copy="doors" className="card-doors" style={{ opacity: 0 }}>
        <h2 className="three-ways">Three ways in.</h2>
        <nav className="products" aria-label="Products">
          {PRODUCTS.map((pr) => (
            <a key={pr.name} href={pr.href} className={`product accent-${pr.accent}`}>
              <span className="mark" aria-hidden="true">&#9679;</span>
              <span className="name">{pr.name}</span>
              <span className="line">{pr.line}</span>
            </a>
          ))}
        </nav>
      </div>
    </div>
  )
}
